import {
  DDIProvision,
  Product,
  ServiceCharge,
  ServiceChargePost,
} from "callable-data-objects";
import { CallableInput } from "Components/Shared";
import AllocationNumberType from "Components/Shared/AllocationNumberType";
import CallableDDITextArea from "Components/Shared/CallableDDITextArea";
import { Field, Formik } from "formik";
import useCheckRole from "Hooks/APICheckRole";
import { useTicket } from "Hooks/Endpoints";
import moment from "moment";
import React, { Fragment } from "react";
import {
  Button,
  Checkbox,
  Dimmer,
  Form,
  Label,
  Loader,
  Menu,
  Segment,
  Table,
} from "semantic-ui-react";
import * as yup from "yup";

const validation = yup.object().shape({
  allocatedNumbers: yup.string().required(),
  activationRef: yup.string().required(),
  portalRef: yup.string().required(),
  status: yup.string().required(),
  numberType: yup.string().required(),
});

const AllocationBuildForm = () => {
  const { hasRole, roles } = useCheckRole();

  const {
    ticket,
    updateAllocation,
    buildAllocation,
    loading,
    charges,
    ticketProductTable,
    addCharge,
    removeCharge,
  } = useTicket();

  const { customer, allocatedNumbers, numberType, ddis } =
    ticket.allocationRequest;

  const getCharge = (s: string, p: string): ServiceCharge => {
    return (
      charges &&
      charges.find((sc: ServiceCharge) => sc.reference === s && sc.name === p)
    );
  };

  const getProduct = (p: string) => {
    return (
      ticketProductTable &&
      ticketProductTable.products.find((pr: Product) => p === pr.name)
    );
  };

  const add = (s: string, p: string) => {
    const product = getProduct(p);
    if (product) {
      const charge: ServiceChargePost = {
        customer: customer,
        reference: s,
        name: product.name,
        type: product.category,
        description: product.description,
        ticketReference: ticket.caseId,
        unit: product.type,
        price: product.price,
        qty: 1,
        total: product.price,
        start: moment().toISOString(),
      };
      addCharge(charge);
    }
  };

  const remove = (s: string, p: string) => {
    const c: ServiceCharge = getCharge(s, p);
    if (c) {
      removeCharge(c.id);
    }
  };

  return (
    <Fragment>
      <Dimmer inverted active={loading}>
        <Loader inverted />
      </Dimmer>
      {hasRole(roles.ticket.roles.edit) && (
        <Segment>
          <Formik
            initialValues={ticket.allocationRequest}
            onSubmit={(values) => {
              updateAllocation(values);
            }}
            validationSchema={validation}
            render={({ handleSubmit, isValid, errors }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <Field
                    name='portalRef'
                    label='Portal Reference'
                    component={CallableInput}
                    disabled={ddis && ddis.length > 0}
                  />

                  <Field
                    name='activationRef'
                    label='Activation Reference'
                    component={CallableInput}
                    disabled={ddis && ddis.length > 0}
                  />

                  <Field
                    name='allocatedNumbers'
                    label='Allocated Numbers'
                    component={CallableDDITextArea}
                    disabled={ddis && ddis.length > 0}
                  />

                  <AllocationNumberType
                    name='numberType'
                    label='Classification'
                    disabled={ddis && ddis.length > 0}
                  />

                  <Field
                    name='status'
                    label='Status'
                    component={CallableInput}
                    disabled={ddis && ddis.length > 0}
                  />

                  <Button
                    primary
                    disabled={!isValid || (ddis && ddis.length > 0)}
                  >
                    Submit
                  </Button>
                </Form>
              );
            }}
          />
        </Segment>
      )}
      {hasRole(roles.ticket.build.view) && (
        <Segment>
          <Menu>
            <Menu.Item header>Provisioning</Menu.Item>
            {hasRole(roles.ticket.build.build) && (
              <Menu.Menu position='right'>
                <Menu.Item>
                  <Button secondary onClick={buildAllocation}>
                    Build
                  </Button>
                </Menu.Item>
              </Menu.Menu>
            )}
          </Menu>
          <Table compact stackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>DDI</Table.HeaderCell>
                <Table.HeaderCell>Built</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {allocatedNumbers &&
                allocatedNumbers.split("\n").map((s: string) => (
                  <Table.Row>
                    <Table.Cell width={14}>{s}</Table.Cell>
                    <Table.Cell width={2}>
                      <Checkbox
                        toggle
                        checked={
                          ddis &&
                          ddis.find((dp: DDIProvision) => dp.ddi.name === s) !==
                            undefined
                        }
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
          </Table>
        </Segment>
      )}
      {hasRole(roles.ticket.billing.view) && (
        <Segment>
          <Menu>
            <Menu.Item header>
              Billing <Label as='tag'>{ticketProductTable?.name}</Label>
            </Menu.Item>
          </Menu>
          <Table compact stackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>DDI</Table.HeaderCell>
                <Table.HeaderCell>Product Name</Table.HeaderCell>
                <Table.HeaderCell>Product Price</Table.HeaderCell>
                {hasRole(roles.ticket.billing.build) && (
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                )}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {allocatedNumbers &&
                allocatedNumbers.split("\n").map((s: string) => (
                  <Table.Row>
                    <Table.Cell width={6}>{s}</Table.Cell>
                    <Table.Cell
                      width={6}
                    >{`DDIAllocation${numberType}`}</Table.Cell>
                    <Table.Cell>
                      {ticketProductTable &&
                        ticketProductTable.products.find(
                          (p: Product) =>
                            p.name === `DDIAllocation${numberType}`
                        )?.price}
                    </Table.Cell>
                    {hasRole(roles.ticket.billing.build) && (
                      <Table.Cell>
                        {getCharge(s, `DDIAllocation${numberType}`) ===
                        undefined ? (
                          <Button
                            secondary
                            onClick={() => add(s, `DDIAllocation${numberType}`)}
                          >
                            Add
                          </Button>
                        ) : (
                          <Button
                            primary
                            onClick={() =>
                              remove(s, `DDIAllocation${numberType}`)
                            }
                          >
                            Remove
                          </Button>
                        )}
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              {allocatedNumbers &&
                allocatedNumbers.split("\n").map((s: string) => (
                  <Table.Row>
                    <Table.Cell width={6}>{s}</Table.Cell>
                    <Table.Cell
                      width={6}
                    >{`DDIRental${numberType}`}</Table.Cell>
                    <Table.Cell>
                      {ticketProductTable &&
                        ticketProductTable.products.find(
                          (p: Product) => p.name === `DDIRental${numberType}`
                        )?.price}
                    </Table.Cell>
                    {hasRole(roles.ticket.billing.build) && (
                      <Table.Cell>
                        {getCharge(s, `DDIRental${numberType}`) ===
                        undefined ? (
                          <Button
                            secondary
                            onClick={() => add(s, `DDIRental${numberType}`)}
                          >
                            Add
                          </Button>
                        ) : (
                          <Button
                            primary
                            onClick={() => remove(s, `DDIRental${numberType}`)}
                          >
                            Remove
                          </Button>
                        )}
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
            </Table.Body>
          </Table>
        </Segment>
      )}
    </Fragment>
  );
};

export default AllocationBuildForm;
